import { generateAlerts, type SystemAlert, type AlertCategory, type AlertSeverity } from "./alertsEngine";
import type { ValidationRuleConfig, ValidationSeverityLevel } from "./SafetyControlContext";
import type { Job, Issue, Contractor, ContractorOpportunity, CustomerCredit } from "./types";
import { isActiveStatus, type Quote } from "./quoteTypes";
import { getStageConfig } from "./workflowEngine";

function mapValidationSeverityToAlertSeverity(level: ValidationSeverityLevel): AlertSeverity {
  if (level === "hard_block") return "critical";
  if (level === "soft_block") return "warning";
  return "info";
}

function mapRuleCategoryToAlertCategory(rule: ValidationRuleConfig): AlertCategory {
  switch (rule.category) {
    case "ordering":
      return "procurement";
    case "pricing":
      return "pricing";
    case "compliance":
      return "integrity";
    case "dispatch":
    case "scheduling":
    case "job_creation":
    case "sales":
    case "insights":
    default:
      return "job";
  }
}

function isOpenJob(job: Job): boolean {
  return (getStageConfig(job.currentStage)?.order ?? 0) < 100;
}

function getJobDisplayRef(job: Job): string {
  return typeof job.jobNumber === "string" && job.jobNumber.trim() ? job.jobNumber.trim() : job.id;
}

function isScheduleCandidate(job: Job): boolean {
  const order = getStageConfig(job.currentStage)?.order ?? 0;
  return isOpenJob(job) && (order >= 50 || Boolean(job.scheduledInstallDate));
}

function isOrderingCandidate(job: Job): boolean {
  const order = getStageConfig(job.currentStage)?.order ?? 0;
  return isOpenJob(job) && order >= 30 && order < 65;
}

function isDispatchCandidate(job: Job): boolean {
  const order = getStageConfig(job.currentStage)?.order ?? 0;
  return isOpenJob(job) && (order >= 65 || job.dispatchStatus === "Ready for Dispatch" || job.dispatchStatus === "Awaiting Response");
}

function hasActiveRescission(job: Job): boolean {
  if (job.rescissionStatus !== "In Rescission") return false;
  if (!job.rescissionEndsAt) return true;
  return new Date(job.rescissionEndsAt).getTime() > Date.now();
}

function hasSignedContract(job: Job): boolean {
  return job.contractDocument?.status === "Signed";
}

function hasUrgentOpenIssue(jobId: string, issues: Issue[]): boolean {
  return issues.some((issue) => issue.jobId === jobId && issue.severity === "Urgent" && (issue.status === "Open" || issue.status === "In Progress"));
}

function hasSchedulingConflict(job: Job, jobs: Job[]): boolean {
  if (!job.scheduledInstallDate) return false;
  return jobs.some((other) => {
    if (other.id === job.id || !isOpenJob(other)) return false;
    if (other.scheduledInstallDate !== job.scheduledInstallDate) return false;
    const sameCrew =
      (job.assignedCrew && other.assignedCrew && job.assignedCrew === other.assignedCrew) ||
      (job.assignedSubcontractorId && other.assignedSubcontractorId && job.assignedSubcontractorId === other.assignedSubcontractorId);
    return sameCrew;
  });
}

function isQuoteActive(quote: Quote): boolean {
  return isActiveStatus(quote.status);
}

function buildRuleAlert(
  rule: ValidationRuleConfig,
  entityId: string,
  title: string,
  description: string,
  actionLink: string,
  timestamp: string,
  refs: Pick<SystemAlert, "jobId" | "quoteId"> = {},
): SystemAlert {
  return {
    id: `rule-alert-${rule.code}-${entityId}`,
    severity: mapValidationSeverityToAlertSeverity(rule.currentSeverity),
    category: mapRuleCategoryToAlertCategory(rule),
    title,
    description,
    actionLabel: "Review",
    actionLink,
    timestamp,
    jobNumber: "",
    ...refs,
  };
}

function generateSafetyRuleAlerts(
  jobs: Job[],
  issues: Issue[],
  quotes: Quote[],
  rules: ValidationRuleConfig[],
): SystemAlert[] {
  const alerts: SystemAlert[] = [];
  const activeRules = rules.filter((rule) => rule.enabled && !rule.archived && rule.alertEnabled);

  for (const rule of activeRules) {
    if (rule.triggerEvent === "schedule_install") {
      for (const job of jobs.filter(isScheduleCandidate)) {
        const jobRef = getJobDisplayRef(job);
        if (rule.code === "SCHED_NO_CONTRACT" && !hasSignedContract(job)) {
          alerts.push(buildRuleAlert(rule, job.id, rule.label, `${jobRef} cannot be scheduled because the contract is not signed.`, `/jobs/${job.id}`, job.updatedAt || new Date().toISOString(), { jobId: job.id }));
        }
        if (rule.code === "SCHED_RESCISSION" && hasActiveRescission(job)) {
          alerts.push(buildRuleAlert(rule, job.id, rule.label, `${jobRef} is still in rescission and should not be scheduled yet.`, `/jobs/${job.id}`, job.updatedAt || new Date().toISOString(), { jobId: job.id }));
        }
        if (rule.code === "SCHED_NO_PERMIT" && job.permitRequired && job.permitStatus !== "Approved") {
          alerts.push(buildRuleAlert(rule, job.id, rule.label, `${jobRef} is missing permit approval for scheduling.`, `/jobs/${job.id}`, job.updatedAt || new Date().toISOString(), { jobId: job.id }));
        }
        if (rule.code === "SCHED_MATERIALS" && ["Missing Items", "Not Ordered", "Field Purchase Required", "Partially Received"].includes(job.materialReadiness)) {
          alerts.push(buildRuleAlert(rule, job.id, rule.label, `${jobRef} is not ready to schedule because materials are "${job.materialReadiness}".`, `/jobs/${job.id}`, job.updatedAt || new Date().toISOString(), { jobId: job.id }));
        }
        if (rule.code === "SCHED_CRITICAL_ISSUES" && hasUrgentOpenIssue(job.id, issues)) {
          alerts.push(buildRuleAlert(rule, job.id, rule.label, `${jobRef} still has urgent open issues that should be resolved before scheduling.`, `/jobs/${job.id}`, job.updatedAt || new Date().toISOString(), { jobId: job.id }));
        }
        if (rule.code === "SCHED_CONFLICT" && hasSchedulingConflict(job, jobs)) {
          alerts.push(buildRuleAlert(rule, job.id, rule.label, `${jobRef} has a scheduling conflict with another assigned job on the same date.`, `/jobs/${job.id}`, job.updatedAt || new Date().toISOString(), { jobId: job.id }));
        }
      }
    }

    if (rule.triggerEvent === "order_materials") {
      for (const job of jobs.filter(isOrderingCandidate)) {
        const jobRef = getJobDisplayRef(job);
        if (rule.code === "ORDER_NO_CONTRACT" && !hasSignedContract(job)) {
          alerts.push(buildRuleAlert(rule, job.id, rule.label, `${jobRef} cannot move into ordering because the contract is not signed.`, `/jobs/${job.id}`, job.updatedAt || new Date().toISOString(), { jobId: job.id }));
        }
        if (rule.code === "ORDER_RESCISSION" && hasActiveRescission(job)) {
          alerts.push(buildRuleAlert(rule, job.id, rule.label, `${jobRef} is still in rescission and should not be ordered yet.`, `/jobs/${job.id}`, job.updatedAt || new Date().toISOString(), { jobId: job.id }));
        }
      }
    }

    if (rule.triggerEvent === "dispatch_crew") {
      for (const job of jobs.filter(isDispatchCandidate)) {
        const jobRef = getJobDisplayRef(job);
        if (rule.code === "DISP_UNASSIGNED" && job.assignmentType === "Unassigned" && !job.assignedSubcontractorId && !job.assignedCrew) {
          alerts.push(buildRuleAlert(rule, job.id, rule.label, `${jobRef} is ready for dispatch but has no crew or subcontractor assigned.`, `/jobs/${job.id}`, job.updatedAt || new Date().toISOString(), { jobId: job.id }));
        }
        if (rule.code === "DISP_NO_DATE" && !job.scheduledInstallDate) {
          alerts.push(buildRuleAlert(rule, job.id, rule.label, `${jobRef} is moving toward dispatch without a scheduled install date.`, `/jobs/${job.id}`, job.updatedAt || new Date().toISOString(), { jobId: job.id }));
        }
        if (rule.code === "DISP_PERMIT" && job.permitRequired && job.permitStatus !== "Approved") {
          alerts.push(buildRuleAlert(rule, job.id, rule.label, `${jobRef} cannot be dispatched until the permit is approved.`, `/jobs/${job.id}`, job.updatedAt || new Date().toISOString(), { jobId: job.id }));
        }
        if (rule.code === "DISP_MATERIALS" && !["Staged for Job", "Loaded for Crew", "Fully Received"].includes(job.materialReadiness)) {
          alerts.push(buildRuleAlert(rule, job.id, rule.label, `${jobRef} is not dispatch-ready because materials are "${job.materialReadiness}".`, `/jobs/${job.id}`, job.updatedAt || new Date().toISOString(), { jobId: job.id }));
        }
        if (rule.code === "DISP_URGENT_ISSUES" && hasUrgentOpenIssue(job.id, issues)) {
          alerts.push(buildRuleAlert(rule, job.id, rule.label, `${jobRef} has urgent open issues that block dispatch.`, `/jobs/${job.id}`, job.updatedAt || new Date().toISOString(), { jobId: job.id }));
        }
      }
    }

    if (rule.triggerEvent === "quote_finalize") {
      for (const quote of quotes.filter(isQuoteActive)) {
        const verification = quote.verification;
        if (rule.code === "PRICE_BELOW_TARGET" && quote.pricingStatus === "Below Target") {
          alerts.push(buildRuleAlert(rule, quote.id, rule.label, `${quote.id} for ${quote.customerName} is below target price.`, `/opportunities/${quote.id}`, quote.updatedAt, { quoteId: quote.id }));
        }
        if (rule.code === "PRICE_LARGE_DISCOUNT" && quote.pricingOverride && Math.abs(quote.pricingOverride.variance) > 15) {
          alerts.push(buildRuleAlert(rule, quote.id, rule.label, `${quote.id} has a ${quote.pricingOverride.variance.toFixed(0)}% discount from calculated price.`, `/opportunities/${quote.id}`, quote.updatedAt, { quoteId: quote.id }));
        }
        if (rule.code === "SALE_POA_MISSING_DOC" && verification?.ownershipType === "POA" && !verification.poaDocumentUploaded) {
          alerts.push(buildRuleAlert(rule, quote.id, rule.label, `${quote.id} is missing the POA document required to finalize the quote.`, `/opportunities/${quote.id}`, quote.updatedAt, { quoteId: quote.id }));
        }
      }
    }
  }

  return alerts;
}

function dedupeAndSortAlerts(alerts: SystemAlert[]): SystemAlert[] {
  const severityOrder: Record<AlertSeverity, number> = { critical: 0, warning: 1, info: 2 };
  const seen = new Set<string>();

  return [...alerts]
    .sort((a, b) => {
      const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
      if (severityDiff !== 0) return severityDiff;
      return b.timestamp.localeCompare(a.timestamp);
    })
    .filter((alert) => {
      if (seen.has(alert.id)) return false;
      seen.add(alert.id);
      return true;
    });
}

export function generateUnifiedAlerts(
  jobs: Job[],
  issues: Issue[],
  contractors: Contractor[],
  opportunities: ContractorOpportunity[],
  credits: CustomerCredit[],
  quotes: Quote[],
  rules: ValidationRuleConfig[] = [],
): SystemAlert[] {
  const systemAlerts = generateAlerts(jobs, issues, contractors, opportunities, credits, quotes);
  const safetyAlerts = generateSafetyRuleAlerts(jobs, issues, quotes, rules);
  return dedupeAndSortAlerts([...systemAlerts, ...safetyAlerts]);
}
